import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add,
  Cancel,
  Delete,
  Edit,
  ExpandMore,
  Lock,
  Refresh,
  Save,
  Business,
} from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import apiClient from 'utils/ApiClient';

export interface IlTenant {
  _id: string;
  companyId: string;
  tenantName: string;
  tenantDescription?: string;
  clientKey: string;
  clientSecret: string;
  createdBy: string;
  updatedBy: string;
  createdAt: string;
  updatedAt: string;
}

type TenantDialogMode = 'add' | 'edit';

const emptyTenantForm = {
  tenantName: '',
  tenantDescription: '',
};

export const TenantsAccordion: React.FC = () => {
  const theme = useTheme();
  const tileGradient = theme.palette.dashboard?.counterHeroGradient
    ?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`;
  const tileForeground = theme.palette.dashboard?.heroTileForegroundColor ?? '#fff';

  const [tenants, setTenants] = useState<IlTenant[]>([]);
  const [tenantSearchQuery, setTenantSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [tenantDialogMode, setTenantDialogMode] = useState<TenantDialogMode>('add');
  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const [tenantFormData, setTenantFormData] = useState(emptyTenantForm);

  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [credentialsTenant, setCredentialsTenant] = useState<IlTenant | null>(null);
  const [refreshSecretConfirmOpen, setRefreshSecretConfirmOpen] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<IlTenant | null>(null);

  const showMessage = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchTenants = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/il/settings/tenants');
      setTenants(Array.isArray(response.data) ? response.data : []);
    } catch (error: any) {
      showMessage(error.message || 'Failed to load tenants', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  const filteredTenants = useMemo(() => {
    const q = tenantSearchQuery.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((tenant) => {
      const haystack = [
        tenant.tenantName,
        tenant.tenantDescription,
        tenant._id,
        tenant.clientKey,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [tenants, tenantSearchQuery]);

  const handleOpenAddTenant = () => {
    setTenantDialogMode('add');
    setEditingTenantId(null);
    setTenantFormData(emptyTenantForm);
    setTenantDialogOpen(true);
  };

  const handleOpenEditTenant = (tenant: IlTenant) => {
    setTenantDialogMode('edit');
    setEditingTenantId(tenant._id);
    setTenantFormData({
      tenantName: tenant.tenantName,
      tenantDescription: tenant.tenantDescription || '',
    });
    setTenantDialogOpen(true);
  };

  const handleTenantDialogClose = () => {
    setTenantDialogOpen(false);
    setEditingTenantId(null);
    setTenantFormData(emptyTenantForm);
  };

  const handleSaveTenant = async () => {
    if (!tenantFormData.tenantName.trim()) {
      showMessage('Tenant name is required', 'error');
      return;
    }

    try {
      setLoading(true);
      if (tenantDialogMode === 'edit' && editingTenantId) {
        await apiClient.patch(`/il/settings/tenant/${editingTenantId}`, tenantFormData);
        showMessage('Tenant updated successfully', 'success');
      } else {
        await apiClient.post('/il/settings/tenant', tenantFormData);
        showMessage('Tenant created successfully', 'success');
      }
      handleTenantDialogClose();
      await fetchTenants();
    } catch (error: any) {
      showMessage(error.message || 'Failed to save tenant', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCredentials = (tenant: IlTenant) => {
    setCredentialsTenant(tenant);
    setCredentialsDialogOpen(true);
  };

  const handleCloseCredentials = () => {
    setCredentialsDialogOpen(false);
    setCredentialsTenant(null);
    setRefreshSecretConfirmOpen(false);
  };

  const handleRefreshClientSecret = async () => {
    if (!credentialsTenant?._id) return;
    try {
      setLoading(true);
      const response = await apiClient.post(
        `/il/settings/tenant/${credentialsTenant._id}/refresh-client-secret`,
      );
      const updated = response.data as IlTenant;
      setCredentialsTenant(updated);
      setTenants((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
      setRefreshSecretConfirmOpen(false);
      showMessage('Client secret refreshed successfully', 'success');
    } catch (error: any) {
      showMessage(error.message || 'Failed to refresh client secret', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (tenant: IlTenant) => {
    setTenantToDelete(tenant);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!tenantToDelete?._id) return;
    try {
      setLoading(true);
      await apiClient.delete(`/il/settings/tenant/${tenantToDelete._id}`);
      showMessage('Tenant deleted successfully', 'success');
      setDeleteConfirmOpen(false);
      setTenantToDelete(null);
      if (credentialsTenant?._id === tenantToDelete._id) {
        handleCloseCredentials();
      }
      await fetchTenants();
    } catch (error: any) {
      showMessage(error.message || 'Failed to delete tenant', 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showMessage(`${label} copied to clipboard`, 'success');
    } catch {
      showMessage(`Failed to copy ${label}`, 'error');
    }
  };

  const accordionLeftColor = theme.palette.dashboard?.welcomePanelLeftTextColor
    ?? (theme.palette.mode === 'light' ? 'black' : 'inherit');
  const accordionRightColor = theme.palette.dashboard?.welcomePanelRightTextColor
    ?? (theme.palette.mode === 'dark' ? 'black' : 'inherit');

  return (
    <>
      <Accordion
        sx={{
          mb: 2,
          borderRadius: '12px !important',
          '&:before': { display: 'none' },
          overflow: 'hidden',
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore sx={{ color: accordionRightColor }} />}
          sx={{
            px: 3,
            py: 1,
            borderRadius: '12px',
            background: theme.palette.dashboard?.welcomePanelGradient
              ?? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            '& .MuiAccordionSummary-content': { my: 1 },
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Business sx={{ color: accordionLeftColor }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: accordionLeftColor }}>
              Tenants
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: { xs: 1.5, sm: 2 }, py: 2 }}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <TextField
              size="small"
              placeholder="Search Tenants"
              value={tenantSearchQuery}
              onChange={(e) => setTenantSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 250, flex: { xs: '1 1 100%', sm: '0 1 auto' } }}
            />
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={handleOpenAddTenant}
              sx={{ textTransform: 'none', whiteSpace: 'nowrap', ml: { xs: 0, sm: 'auto' } }}
            >
              New
            </Button>
          </Paper>

          {filteredTenants.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {tenantSearchQuery
                  ? 'No tenants match your search.'
                  : 'No tenants configured yet. Use the New button to add a tenant.'}
              </Typography>
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: 'text.secondary' }}>
                Configured Tenants
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                Each tenant receives a unique Tenant ID and API credentials (client key and client secret).
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {filteredTenants.map((tenant) => (
                  <Card
                    key={tenant._id}
                    sx={{
                      width: 240,
                      borderRadius: 2,
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        background: tileGradient,
                        color: tileForeground,
                        px: 2,
                        py: 1.5,
                        minHeight: 72,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700} noWrap>
                        {tenant.tenantName}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
                        ID: {tenant._id}
                      </Typography>
                    </Box>
                    <CardContent sx={{ flex: 1, py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 1,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: 40,
                        }}
                      >
                        {tenant.tenantDescription || 'No description'}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        Updated: {new Date(tenant.updatedAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, py: 0.75, gap: 0.5 }}>
                      <Tooltip title="View credentials">
                        <Button
                          color="warning"
                          variant="contained"
                          size="small"
                          onClick={() => handleOpenCredentials(tenant)}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                          aria-label="View credentials"
                        >
                          <Lock sx={{ fontSize: 16 }} />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <Button
                          color="secondary"
                          variant="contained"
                          size="small"
                          onClick={() => handleOpenEditTenant(tenant)}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                          aria-label="Edit tenant"
                        >
                          <Edit sx={{ fontSize: 16 }} />
                        </Button>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button
                          color="error"
                          variant="contained"
                          size="small"
                          onClick={() => handleOpenDeleteConfirm(tenant)}
                          sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}
                          aria-label="Delete tenant"
                        >
                          <Delete sx={{ fontSize: 16 }} />
                        </Button>
                      </Tooltip>
                    </Box>
                  </Card>
                ))}
              </Box>
            </Paper>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog
        open={tenantDialogOpen}
        onClose={handleTenantDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" component="span" fontWeight={600}>
            {tenantDialogMode === 'edit' ? 'Edit Tenant' : 'New Tenant'}
          </Typography>
          <IconButton onClick={handleTenantDialogClose} size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Tenant Name"
            value={tenantFormData.tenantName}
            onChange={(e) => setTenantFormData((prev) => ({ ...prev, tenantName: e.target.value }))}
            fullWidth
            required
            margin="normal"
          />
          <TextField
            label="Tenant Description"
            value={tenantFormData.tenantDescription}
            onChange={(e) => setTenantFormData((prev) => ({ ...prev, tenantDescription: e.target.value }))}
            fullWidth
            multiline
            minRows={3}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={handleTenantDialogClose} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveTenant}
            startIcon={<Save />}
            variant="contained"
            color="success"
            disabled={!tenantFormData.tenantName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={credentialsDialogOpen}
        onClose={handleCloseCredentials}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" component="span" fontWeight={600}>
            Tenant Credentials
          </Typography>
          <IconButton onClick={handleCloseCredentials} size="small" edge="end">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {credentialsTenant?.tenantName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            Tenant ID: {credentialsTenant?._id}
          </Typography>
          <TextField
            label="Client Key"
            value={credentialsTenant?.clientKey || ''}
            fullWidth
            margin="normal"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Copy client key"
                    onClick={() => copyToClipboard(credentialsTenant?.clientKey || '', 'Client key')}
                    edge="end"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Client Secret"
            value={credentialsTenant?.clientSecret || ''}
            fullWidth
            margin="normal"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Copy client secret"
                    onClick={() => copyToClipboard(credentialsTenant?.clientSecret || '', 'Client secret')}
                    edge="end"
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<Refresh />}
            onClick={() => setRefreshSecretConfirmOpen(true)}
          >
            Refresh Secret
          </Button>
          <Button variant="outlined" color="secondary" onClick={handleCloseCredentials} startIcon={<Cancel />}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={refreshSecretConfirmOpen}
        onClose={() => setRefreshSecretConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Refresh Client Secret?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            This will generate a new client secret for &quot;{credentialsTenant?.tenantName}&quot;.
            Applications using the current secret will stop working until updated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setRefreshSecretConfirmOpen(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button variant="contained" color="warning" startIcon={<Refresh />} onClick={handleRefreshClientSecret}>
            Confirm Refresh
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Are you sure you want to delete tenant &quot;{tenantToDelete?.tenantName}&quot;?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action is not recoverable. The tenant record and its credentials will be permanently removed.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => setDeleteConfirmOpen(false)} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button variant="contained" color="error" startIcon={<Delete />} onClick={handleConfirmDelete}>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Backdrop sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TenantsAccordion;
